import { Injectable, inject } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, IHttpConnectionOptions, LogLevel } from '@microsoft/signalr';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { ConfigService } from 'src/config';
import { Garage } from '../../shared/models/garage';
import { AssignmentState, CurrentAssignment, StateOfChargeMeasurementPoint, Vehicle } from '../../shared/models/vehicle';
import { LoggingService } from '../../shared/services/logging.service';
import { VehicleStore } from '../store';
import { LineStore } from '../store/line.store';
import { AppStateService } from './app-state.service';
import { SignalRHubHelper } from './signalr-hub.helper';
import { TrafficDeviationsStore } from '../../../components/traffic-deviations/stores/traffic-deviations.store';
import { NotificationService } from '@modules/shared/services/notification.service';
import { MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { MsalService } from '@azure/msal-angular';
import { ActorRefFrom, createActor } from 'xstate';
import { createSignalRHubMachine } from './signalr-hub.machine';
import { DataSyncService } from './data-sync.service';
import { isEmptyError } from './service.helpers';
import { InteractionRequiredAuthError, BrowserAuthError, BrowserAuthErrorCodes } from '@azure/msal-browser';
import { EvSocPrognosisStore } from '../store/ev-soc-prognosis.store';

@Injectable({
    providedIn: 'root'
})
export class SignalRHubService {
    private readonly _appStateService = inject(AppStateService);
    private readonly _vehicleStore = inject(VehicleStore);
    private readonly _lineStore = inject(LineStore);
    private readonly _configService = inject(ConfigService);
    private readonly _logger = inject(LoggingService);
    private readonly _trafficDeviationStore = inject(TrafficDeviationsStore);
    private readonly _notificationService = inject(NotificationService);
    private readonly _msalService = inject(MsalService);
    private readonly _dataSyncService = inject(DataSyncService);
    private readonly _evSocPrognosisStore = inject(EvSocPrognosisStore);


    public messageReceived$: Observable<string>;

    private _connection: HubConnection;
    private readonly _url: string;
    private _groups: Garage[] = [];
    private _garages: Garage[];

    private readonly _isConnected$: BehaviorSubject<boolean> = new BehaviorSubject(undefined);
    private readonly _messageReceived$ = new Subject<string>();
    private _lockResolver: Function;
    private _reconnectAttemptCounter = 0;
    private _wasReconnect = false;
    private _snackBarRef: MatSnackBarRef<TextOnlySnackBar>;
    private _selectedGaragesStateSubscription: Subscription;
    private _garagesStateSubscription: Subscription;
    private _machineActor: ActorRefFrom<ReturnType<typeof createSignalRHubMachine>>;
    private readonly _signalrSubscriptions = [
        'VehiclePositions',
        'VehicleDeviations',
        'VehicleWarnings',
        'StateOfChargeMessages',
        'ChargingWarnings',
        'LowStateOfChargeWarnings',
        'LowStateOfChargePrognosisWarningMessages',
        'TrafficDeviations',
        'TrafficDeviationReceipts',
        'VehicleSignOnStatus'
    ];


    constructor() {
        this._url = this._configService.signalRHub;
        this.messageReceived$ = this._messageReceived$.asObservable();
    }

    public init() {
        const connection = this._createConnection();

        if (!connection) {
            return;
        }

        const signalRHubMachine = createSignalRHubMachine({
            startConnection: () => this._connection.start(),
            syncGroups: (selected, subscribed) => this._syncGroups(selected, subscribed),
            onConnected: (garages) => this._handleConnected(garages),
            onConnectedError: (error) => this._handleConnectedError(error),
            onReconnecting: (error) => this._handleReconnecting(error),
            onReconnected: () => this._handleReconnected(),
            onClearStaleWarnings: () => this._handleClearStaleWarnings(),
            onDisconnected: (error) => this._handleDisconnected(error),
            onGroupsChanged: (groups) => this._applyGroups(groups),
            onSyncError: (error) => this._handleSyncError(error),
        });

        this._machineActor = createActor(signalRHubMachine);

        this._connection.onreconnecting((error) => {
            this._machineActor?.send({ type: 'RECONNECTING', error });
        });

        this._connection.onreconnected(() => {
            this._machineActor?.send({ type: 'RECONNECTED' });
        });

        this._connection.onclose((error) => {
            this._machineActor?.send({ type: 'CONNECTION_CLOSED', error });
        });

        this._machineActor.subscribe(state => {
            console.log(`SignalR Connection state: ${state.value} with connectionId: ${this._connection?.connectionId}`);
        });

        this._machineActor.start();
        this._listenToGarageChange();
        this._listenToGaragesState();
    }

    get isConnected$(): Observable<boolean> {
        return this._isConnected$.asObservable();
    }

    get connectionId(): string {
        return this._connection?.connectionId;
    }

    private _createConnection() {
        if (!this._url) {
            // if url is undefined, then do nothing
            this._logger.logTrace('WARNING: HUB has no connectionURL');
            return;
        }

        this._connection = new HubConnectionBuilder()
            .withUrl(this._url, {
                withCredentials: true,
                accessTokenFactory: async () => {
                    await this._msalService.instance.initialize();

                    try {
                        const response = await this._msalService.instance.acquireTokenSilent({
                            scopes: [this._configService.signalRScope],
                            account: this._msalService.instance.getActiveAccount(),
                            redirectUri: window.location.origin + '/blank'
                        });

                        return response?.accessToken;
                    } catch (error) {
                        if (isEmptyError(error)) {
                            return;
                        }
                        if (error instanceof InteractionRequiredAuthError) {
                            // Don't trigger redirect if interaction is already in progress.
                            if (error.errorCode !== BrowserAuthErrorCodes.interactionInProgress) {
                                this._msalService.acquireTokenRedirect({
                                    scopes: [this._configService.signalRScope],
                                    redirectUri: window.location.origin + '/blank'
                                });
                            }
                            return;
                        }
                        // Silently handle other MSAL errors in SignalR token factory
                        if (error instanceof BrowserAuthError) {
                            console.warn('MSAL error in SignalR token factory:', error.errorCode);
                            return;
                        }

                        throw error;
                    }
                },
            } as IHttpConnectionOptions)
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: retryContext => {
                    this._reconnectAttemptCounter = retryContext.previousRetryCount + 1;
                    this._showReconnectNotice(this._reconnectAttemptCounter);

                    if (retryContext.elapsedMilliseconds < 60000) {
                        // If we've been reconnecting for less than 60 seconds so far,
                        // wait 5 seconds before the next reconnect attempt.
                        return 5 * 1000;
                    } else if (retryContext.elapsedMilliseconds < 60000 * 10) {
                        // If we've been reconnecting for less than 10 minutes so far,
                        // wait 30 seconds before the next reconnect attempt.
                        return 30 * 1000;
                    } else if (retryContext.elapsedMilliseconds < 60000 * 60) {
                        // If we've been reconnecting for less than 1 hour so far,
                        // wait 5 minutes before the next reconnect attempt.
                        return 60 * 5 * 1000;
                    } else if (retryContext.elapsedMilliseconds < 60000 * 60 * 10) {
                        // If we've been reconnecting for less than 10 hour so far,
                        // wait 10 minutes before the next reconnect attempt.
                        return 60 * 10 * 1000;
                    } else {
                        // If we've been reconnecting for more than 10 hours so far, stop reconnecting.
                        console.error('Trying to reconnect for 10 hours. Closing..');
                        this._logger.logTrace('WARNING: Trying to reconnect for 10 hours. Closing..');
                        return null;
                    }
                }
            })
            .configureLogging(LogLevel.Information)
            .build();

        //#region Listen to these methods on signalR
        this._connection.on('HandleVehiclePosition', (response: any) => {
            this._handlevehiclePositionCallback(response);
            this._messageReceived$.next('HandleVehiclePosition');
        });

        this._connection.on('HandleDeviation', (response: any) => {
            this._handleDeviation(response);
            this._messageReceived$.next('HandleDeviation');
        });

        this._connection.on('HandleTrafficDeviation', (response: any) => {
            this._handleTrafficDeviation(response);
            this._messageReceived$.next('HandleTrafficDeviation');
        });

        this._connection.on('HandleTrafficDeviationReceipt', (response: any) => {
            this._handleTrafficDeviationReceipt(response);
            this._messageReceived$.next('HandleTrafficDeviationReceipt');
        });

        this._connection.on('HandleVehicleWarning', (response: any) => {
            this._handleVehicleWarning(response);
            this._messageReceived$.next('HandleVehicleWarning');
        });

        this._connection.on('HandleParkingWarning', (response: any) => {
            this._handleParkingSpotWarning(response);
            this._messageReceived$.next('HandleParkingWarning');
        });

        this._connection.on('HandleParkingWarningRestored', (response: any) => {
            this._handleParkingWarningRestored(response);
            this._messageReceived$.next('HandleParkingWarningRestored');
        });

        this._connection.on('HandleLowStateOfChargeWarning', (response: any) => {
            this._handleLowSocWarning(response);
            this._messageReceived$.next('HandleLowStateOfChargeWarning');
        });

        this._connection.on('HandleLowStateOfChargeWarningRestored', (response: any) => {
            this._handleLowSocWarningRestored(response);
            this._messageReceived$.next('HandleLowStateOfChargeWarningRestored');
        });

        this._connection.on('HandleLowStateOfChargePrognosisWarnings', (response: any) => {
            this._handleLowSocPrognosisWarning(response);
            this._messageReceived$.next('HandleLowStateOfChargePrognosisWarnings');
        });

        this._connection.on('HandleVehicleWarningRestored', (response: any) => {
            this._handleVehicleWarningRestored(response);
            this._messageReceived$.next('HandleVehicleWarningRestored');
        });

        this._connection.on('HandleStateOfChargeMessage', (response: any) => {
            this._handleHandleVehicleSoc(response);
            this._messageReceived$.next('HandleStateOfChargeMessage');
        });

        this._connection.on('HandleVehicleTrafficStatus', (response: any) => {
            this._handleVehicleTrafficStatus(response);
            this._messageReceived$.next('HandleVehicleTrafficStatus');
        });

        this._connection.on('HandleSignOnStatus', (response: any) => {
            this._handleSignOnStatus(response);
            this._messageReceived$.next('HandleSignOnStatus');
        });
        //#endregion

        return this._connection;
    }

    private _listenToGarageChange() {
        if (this._selectedGaragesStateSubscription) {
            this._selectedGaragesStateSubscription.unsubscribe();
        }

        this._selectedGaragesStateSubscription = this._appStateService.getSelectedGaragesState()
            .subscribe((selectedGarages: Garage[]) => {
                this._machineActor?.send({
                    type: 'SELECTED_GARAGES_UPDATED',
                    garages: selectedGarages,
                });
            });
    }

    private _listenToGaragesState() {
        if (this._garagesStateSubscription) {
            this._garagesStateSubscription.unsubscribe();
        }

        this._garagesStateSubscription = this._appStateService.getGaragesState()
            .subscribe(garages => {
                this._garages = garages;
            });
    }

    private async _syncGroups(selected: Garage[], subscribed: Garage[]) {
        if (this._connection.state !== HubConnectionState.Connected) {
            return;
        }

        const added = selected.filter(g => !subscribed.some(s => s.id === g.id));
        const removed = subscribed.filter(g => !selected.some(s => s.id === g.id));

        if (added.length) {
            await this._subscribe(added);
        }

        if (removed.length) {
            await this._unsubscribe(removed);
        }
    }

    private _getSignalRGroups(garages: Garage[]): string[] {
        const groups: string[] = [];
        for (const subscription of this._signalrSubscriptions) {
            if (subscription === 'StateOfChargeMessages' || subscription === 'ChargingWarnings' || subscription === 'LowStateOfChargeWarnings' || subscription === 'LowStateOfChargePrognosisWarningMessages') {
                groups.push(...garages.map(g => `${subscription}.${g.id}`));
            } else {
                groups.push(...garages.map(g => `${subscription}.${g.code}`));
            }
        }
        return groups;
    }

    private async _subscribe(garages: Garage[]) {
        if (!garages?.length) return;

        const maxRetries = 3;
        const groups = this._getSignalRGroups(garages);

        const retrySubscription = async (attempts = 0): Promise<void> => {
            try {
                await this._connection.invoke(
                    'SubscribeToGroups', 
                    this._connection.connectionId, 
                    groups
                );
            } catch (error) {
                if (attempts < maxRetries) {
                    await new Promise(resolve => setTimeout(resolve, 1000 * (attempts + 1)));
                    return retrySubscription(attempts + 1);
                }

                if (this._isConnectionGone(error)) {
                    this._logger.logTrace(`Failed to subscribe to SignalR Subjects after ${attempts + 1} attempts (connection already gone): ${error}`);
                } else {
                    this._logger.logException(new Error(`Failed to subscribe to SignalR Subjects after ${attempts + 1} attempts: ${error}`));
                }
            }
        };

        await retrySubscription();

    }

    private async _unsubscribe(garages: Garage[]) {
        if (!garages?.length) return;

        const maxRetries = 3;
        const groups = this._getSignalRGroups(garages);

        const retryUnsubscribe = async (attempts = 0): Promise<void> => {
            try {
                await this._connection.invoke(
                    'UnSubscribeFromGroups', 
                    this._connection.connectionId, 
                    groups); 
            } catch (error) {
                if (attempts < maxRetries) {
                    await new Promise(resolve => setTimeout(resolve, 1000 * (attempts + 1)));
                    return retryUnsubscribe(attempts + 1);
                }

                if (this._isConnectionGone(error)) {
                    this._logger.logTrace(`Failed to unsubscribe from SignalR Subjects after ${attempts + 1} attempts (connection already gone): ${error}`);
                } else {
                    this._logger.logException(new Error(`Failed to unsubscribe from SignalR Subjects after ${attempts + 1} attempts: ${error}`));
                }
            }
        };
        
        await retryUnsubscribe();

        for (const garage of garages) {
            // Remove the selected garages from the store
            this._vehicleStore.remove(garage.code);
            this._lineStore.remove(garage.code);
            this._trafficDeviationStore.remove(garage.code);
            this._evSocPrognosisStore.remove(garage.id);
        }

    }

    private _applyGroups(groups: Garage[]) {
        this._groups = groups ?? [];

        if (this._groups.length) {
            this._createWebLock();
            return;
        }

        if (this._lockResolver) {
            this._lockResolver();
            this._lockResolver = undefined;
        }
    }

    private async _handleConnected(garages: Garage[]) {
        this._isConnected$.next(true);
        this._clearReconnectNotice();

        if (garages?.length) {
            const isReconnect = this._wasReconnect;
            this._wasReconnect = false;
            await this._dataSyncService.publishCurrentState(garages, this._connection.connectionId, isReconnect);
            await this._dataSyncService.publishLowSocPrognosisWarnings(
                garages,
                this._connection.connectionId,
                isReconnect,
            );
        }
    }

    private _handleReconnecting(error?: unknown) {
        this._isConnected$.next(false);

        if (error) {
            if (this._isAbnormalClose(error)) {
                this._logger.logTrace(`SignalR reconnecting after abnormal close (1006): ${error}`);
            } else {
                this._logger.logException(error instanceof Error ? error : new Error(`SignalR reconnecting: ${error}`));
            }
        }
    }

    private _handleReconnected() {
        this._wasReconnect = true;
        // Mark all warnings as stale to ensure that they are refreshed on the UI after a reconnect, since we might have missed some warning updates while being disconnected.
        this._vehicleStore.markAllWarningsAsStale();
    }

    private _handleClearStaleWarnings() {
        // Clear warnings that are still marked as stale after 15 seconds, meaning the backend did not re-send them (they were deleted).
        this._vehicleStore.clearStaleWarnings();
    }

    private _handleDisconnected(error?: unknown) {
        this._isConnected$.next(false);

        if (error) {
            if (this._isAbnormalClose(error)) {
                this._logger.logTrace(`SignalR connection closed abnormally (1006): ${error}`);
            } else {
                this._logger.logException(error instanceof Error ? error : new Error(`SignalR connection closed: ${error}`));
            }
        }
    }

    private _isAbnormalClose(error: unknown): boolean {
        return error instanceof Error && error.message.includes('1006');
    }

    private _isConnectionGone(error: unknown): boolean {
        return error instanceof Error && error.message.includes('404');
    }

    private _handleSyncError(error?: unknown) {
        this._logger.logException(error instanceof Error ? error : new Error(`SignalR group sync failed: ${error}`));
    }

    private _handleConnectedError(error?: unknown) {
        this._logger.logException(error instanceof Error ? error : new Error(`SignalR onConnected failed: ${error}`));
    }

    private _showReconnectNotice(attempt: number) {
        this._snackBarRef?.dismiss();
        this._snackBarRef = this._notificationService.openError(
            `SignalR connection lost. Trying to reconnect.. attempt: ${attempt}`,
            0
        );
    }

    private _clearReconnectNotice() {
        this._snackBarRef?.dismiss();
        this._snackBarRef = undefined;
        this._reconnectAttemptCounter = 0;
    }

    //#region Handle methods from signalR
    private _handlevehiclePositionCallback(response: string) {
        const data = JSON.parse(response);
        const vehicles = SignalRHubHelper.getVehiclesFromPositionMessage(data, this._groups.map(({ code }) => code));

        this._vehicleStore.update(vehicles);

        vehicles.forEach(vehicle => {
            if(!vehicle.position?.latitude || !vehicle.position.longitude) {
                this._logger.logEvent('Received vehicle with no position', {
                    garageCode: vehicle.garageCode,
                    inventoryNumber: vehicle.inventoryNumber,
                });
            }
        });
    }

    private _handleDeviation(response: string) {
        const data = JSON.parse(response);
        const vehicles = SignalRHubHelper.getVehiclesFromDeviationMessage(data, this._garages, this._groups.map(({ code }) => code));

        this._vehicleStore.update(vehicles);
    }

    private _handleTrafficDeviation(response: string) {
        const data = JSON.parse(response);

        const deviations = SignalRHubHelper.getDeviationsFromTrafficDeviationMessage(data, this._groups.map(({ code }) => code));

        this._trafficDeviationStore.updateTrafficDeviation(deviations);
    }

    private _handleTrafficDeviationReceipt(response: string) {
        const data = JSON.parse(response);

        const receipts = SignalRHubHelper.getReceiptsFromTrafficDeviationReceiptMessage(data, this._groups.map(({ code }) => code));

        this._trafficDeviationStore.updateTrafficDeviationReceipt(receipts);
    }

    private _handleVehicleWarning(response: string) {
        const data = JSON.parse(response);

        const vehicles = SignalRHubHelper.getVehiclesFromWarningMessage(data, this._groups.map(({ code }) => code),
            this._configService.minuteFilterForFetchingWarningData, this._configService.disabledWarningTypes);

        this._vehicleStore.update(vehicles);
    }

    private _handleParkingSpotWarning(response: string) {
        const data = JSON.parse(response);

        const vehicles = SignalRHubHelper.getVehiclesFromParkingSpotWarningMessage(data, this._groups.map(({ id }) => id),
            this._configService.minuteFilterForFetchingWarningData);

        this._vehicleStore.update(vehicles);
    }
    private _handleLowSocWarning(response: string) {
        const data = JSON.parse(response);

        const vehicles = SignalRHubHelper.getVehiclesFromLowSocWarningMessage(data, this._groups.map(({id})  => id ),
            this._configService.minuteFilterForFetchingWarningData);

        this._vehicleStore.update(vehicles);
    }
    private _handleLowSocWarningRestored(response: string) {
        const data = JSON.parse(response);

        const vehicles = SignalRHubHelper.getVehiclesFromLowSocWarningRestoredMessage(data);

        this._vehicleStore.update(vehicles);
    }

    private _handleLowSocPrognosisWarning(response: string) {
        try {
            const data = JSON.parse(response);
            const prognosis = SignalRHubHelper.mapLowSocPrognosisWarningMessage(
                data,
                this._groups.map(({ id }) => id),
            );

            if (!prognosis) {
                return;
            }

            this._evSocPrognosisStore.setSnapshot(prognosis);
        } catch (error) {
            this._logger.logException(error instanceof Error ? error : new Error(`Failed to parse EV-SOC prognosis payload: ${error}`));
        }
    }


    private _handleVehicleWarningRestored(response: string) {
        const data = JSON.parse(response);

        const vehicles = SignalRHubHelper.getVehiclesFromWarningRestoredMessage(data, this._groups.map(({ code }) => code),
            this._configService.minuteFilterForFetchingWarningData, this._configService.disabledWarningTypes);

        this._vehicleStore.update(vehicles);
    }

    private _handleParkingWarningRestored(response: string) {
        const data = JSON.parse(response);

        const vehicles = SignalRHubHelper.getVehiclesFromParkingSpotWarningRestoredMessage(data, this._groups.map(({id})  => id ));

        this._vehicleStore.update(vehicles);
    }

    private _handleVehicleTrafficStatus(response: string) {
        const data = JSON.parse(response);

        const vehicles = _.chain(data)
            .filter((item) => this._isInGarageSelection(item.garageCode))
            .map((item) => {
                let trafficAreaName;
                const garage = this._garages.find(g => g.code === data.garageCode);
                if (garage) {
                    trafficAreaName = garage.trafficAreaName;
                }

                const vehicle = new Vehicle({
                    garageCode: item.garageCode,
                    inventoryNumber: item.inventoryNumber,
                    blockNumber: item.activeBlockNumber,
                    routeNumber: item.activeRouteNumber,
                    tripNumber: item.activeTripNumber,
                    trafficAreaName: trafficAreaName,
                    onVehicleTaskBlock: item.onVehicleTaskBlock,
                    onTrip: item.onTrip
                });

                return vehicle;

            }).value();

        this._vehicleStore.update(vehicles);
    }

    private _handleSignOnStatus(response: string) {
        const data = JSON.parse(response);
        const items = Array.isArray(data) ? data : [data];


        const vehicles = _.chain(items)
            .filter((item: any) => this._isInGarageSelection(item.garageCode))
            .map((item: any) => {
                const metadata = item.metadata ?? {};
                const normalizedInventoryNumber = this._normalizeVehicleId(item.inventoryNumber);
                const assignmentState = Number(metadata.state);
                const isSignedOn = assignmentState === AssignmentState.SignedOn;
                const currentAssignment: CurrentAssignment = {
                    state: Number.isFinite(assignmentState) ? assignmentState : AssignmentState.Unknown,
                    stateName: metadata.stateName ?? 'Unknown',
                    tripActorId: isSignedOn ? metadata.tripActorId : '',
                    routeName: isSignedOn ? metadata.routeName : undefined,
                    trafficDate: metadata.trafficDate,
                    tripNumber: isSignedOn ? metadata.tripNumber : undefined,
                    vehicleId: normalizedInventoryNumber,
                    garageCode: item.garageCode,
                    lastChangedAt: item.measuredAt,
                    derivedFromMessageId: metadata.derivedFromMessageId,
                    lineName: isSignedOn ? metadata.lineName ?? metadata.routeName : '',
                    plannedStartTime: isSignedOn ? metadata.plannedStartTime : '',
                    plannedEndTime: isSignedOn ? metadata.plannedEndTime : '',
                    pendingTripActorIds: metadata.pendingTripActorIds ?? [],
                };

                const garage = this._garages?.find(g => g.code === item.garageCode);

                return new Vehicle({
                    inventoryNumber: normalizedInventoryNumber,
                    garageCode: item.garageCode,
                    garageId: item.garageId ?? garage?.id,
                    currentAssignment,
                });
            })
            .value();

        this._vehicleStore.update(vehicles);
    }

    private _handleHandleVehicleSoc(response: string) {
        const data = JSON.parse(response);
        const vehicles = _.chain(data)
        .filter((item) => this._isInGarageIdSelection(item.garageId))
            .map((item) => {
                const vehicle = new Vehicle({
                    inventoryNumber: item.inventoryNumber,
                    garageId : item.garageId,
                    stateOfCharge: new StateOfChargeMeasurementPoint({ sensorValue: item.stateOfCharge, measuredAt: item.measuredAt })
                });
                return vehicle;
            }).value();

        this._vehicleStore.update(vehicles);
    }

    private _isInGarageSelection(garageCode: string): boolean {
        const lower = this._groups.map(({ code }) => code.toLowerCase())
        return _.includes(lower, garageCode.toLowerCase());
    }

    private _normalizeVehicleId(inventoryNumber: string): string {
        const source = (inventoryNumber ?? '').trim();
        if (!source) return '';

        const digits = source.replace(/\D/g, '');
        return digits || source;
    }

    private _isInGarageIdSelection(garageId: number): boolean {
        const ids = this._groups.map( (item) => item.id)
        return _.includes(ids, garageId);
    }

    private _createWebLock(): void {
        if (navigator?.locks?.request && !this._lockResolver) {
            const promise = new Promise((res: Function) => {
                this._lockResolver = res;
            });

            navigator.locks.request('signalr-web-lock', { mode: 'shared' }, () => {
                return promise;
            });
        }
    }
}
